import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStrings } from '../i18n';
import { useAuth } from '../context/AuthContext';
import PanchTatvaButton from '../components/PanchTatvaButton';
import { downloadReceiptPdf } from '../utils/receiptPdf';
import {
  getReceipt,
  ReceiptDisabledError,
  ReceiptRateLimitError,
  ReceiptForbiddenError,
  ReceiptNotFoundError,
} from '../services/receiptService';

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function ReceiptHeader({ receipt, strings }) {
  const issued = new Date(receipt.issuedAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 pb-8 border-b border-cardBg print:pb-6">
      {/* Brand */}
      <div>
        <p className="text-2xl font-heading text-headingText tracking-wide">Panch Tatva</p>
        <p className="text-sm text-textSecondary font-sans mt-1">by Anukta</p>
      </div>

      {/* Receipt meta */}
      <div className="text-right">
        <h1 className="text-3xl md:text-4xl font-heading text-headingText mb-2">
          {strings.receipt.title}
        </h1>
        <p className="text-sm text-textSecondary font-sans">
          <span className="font-semibold text-bodyText">{strings.receipt.receiptNumber}</span>{' '}
          {receipt.receiptNumber}
        </p>
        <p className="text-sm text-textSecondary font-sans">
          <span className="font-semibold text-bodyText">{strings.receipt.issuedDate}:</span>{' '}
          {issued}
        </p>
        <p className="text-sm text-textSecondary font-sans">
          <span className="font-semibold text-bodyText">{strings.receipt.orderId}:</span>{' '}
          {receipt.orderId}
        </p>
      </div>
    </div>
  );
}

function ReceiptParties({ receipt, strings }) {
  const { customer, billingAddress } = receipt;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-8 border-b border-cardBg print:py-6">
      {/* Customer */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-textSecondary mb-2 font-sans">
          {strings.receipt.customerInfo}
        </p>
        <p className="text-bodyText font-semibold font-sans">{customer.name}</p>
        <p className="text-textSecondary text-sm font-sans">{customer.email}</p>
      </div>

      {/* Billing address */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-textSecondary mb-2 font-sans">
          {strings.receipt.billingAddress}
        </p>
        <p className="text-bodyText font-sans text-sm leading-relaxed">
          {billingAddress.line1}
          <br />
          {billingAddress.city}, {billingAddress.state} — {billingAddress.pincode}
          <br />
          {billingAddress.country}
        </p>
      </div>
    </div>
  );
}

function ReceiptTable({ receipt, strings }) {
  const fmt = (amount) =>
    `${strings.receipt.currency}${Number(amount).toLocaleString('en-IN')}`;

  return (
    <div className="py-8 border-b border-cardBg print:py-6">
      <p className="text-xs font-semibold uppercase tracking-widest text-textSecondary mb-4 font-sans">
        {strings.receipt.orderItems}
      </p>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm font-sans">
          <thead>
            <tr className="border-b border-cardBg text-textSecondary text-left">
              <th className="pb-3 pr-4 font-semibold">{strings.receipt.columnProduct}</th>
              <th className="pb-3 pr-4 font-semibold text-center w-16">{strings.receipt.columnQty}</th>
              <th className="pb-3 pr-4 font-semibold text-right">{strings.receipt.columnUnit}</th>
              <th className="pb-3 font-semibold text-right">{strings.receipt.columnTotal}</th>
            </tr>
          </thead>
          <tbody>
            {receipt.items.map((item) => (
              <tr key={item.id} className="border-b border-cardBg last:border-0">
                <td className="py-3 pr-4 text-bodyText font-medium">{item.name}</td>
                <td className="py-3 pr-4 text-center text-bodyText">{item.quantity}</td>
                <td className="py-3 pr-4 text-right text-bodyText">{fmt(item.price)}</td>
                <td className="py-3 text-right text-bodyText font-semibold">{fmt(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Price breakdown */}
      <div className="mt-6 flex flex-col items-end gap-2">
        <div className="w-full max-w-xs space-y-2">
          <div className="flex justify-between text-sm font-sans text-bodyText">
            <span>{strings.receipt.subtotal}</span>
            <span>{fmt(receipt.subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm font-sans text-bodyText">
            <span>{strings.receipt.tax}</span>
            <span>{receipt.tax === 0 ? strings.receipt.free : fmt(receipt.tax)}</span>
          </div>
          <div className="flex justify-between text-sm font-sans text-bodyText">
            <span>{strings.receipt.shipping}</span>
            <span>{receipt.shipping === 0 ? strings.receipt.free : fmt(receipt.shipping)}</span>
          </div>
          <div className="flex justify-between text-base font-bold font-sans text-secondary border-t border-secondary pt-3 mt-2">
            <span>{strings.receipt.total}</span>
            <span>{fmt(receipt.total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReceiptPayment({ receipt, strings }) {
  const { payment } = receipt;

  return (
    <div className="py-8 print:py-6">
      <p className="text-xs font-semibold uppercase tracking-widest text-textSecondary mb-4 font-sans">
        {strings.receipt.paymentInfo}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm font-sans">
        <div>
          <p className="text-textSecondary">{strings.receipt.paymentMethod}</p>
          <p className="text-bodyText font-semibold">{payment.method}</p>
        </div>
        <div>
          <p className="text-textSecondary">{strings.receipt.paymentId}</p>
          <p className="text-bodyText font-semibold break-all">{payment.paymentId}</p>
        </div>
        <div>
          <p className="text-textSecondary">{strings.receipt.paymentStatus}</p>
          <span className="inline-block mt-1 px-3 py-0.5 rounded-full bg-secondary text-surface text-xs font-semibold uppercase tracking-wide">
            {payment.status === 'paid' ? strings.receipt.paid : payment.status}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Loading skeleton
// ─────────────────────────────────────────────────────────────────────────────

function ReceiptSkeleton() {
  const bar = (w) => (
    <div className={`h-4 bg-cardBg rounded animate-pulse ${w}`} />
  );
  return (
    <div className="bg-surface rounded-2xl shadow-card p-6 md:p-10 space-y-8">
      {/* Header */}
      <div className="flex justify-between pb-8 border-b border-cardBg">
        <div className="space-y-2">{bar('w-32')}{bar('w-20')}</div>
        <div className="space-y-2 text-right">{bar('w-24')}{bar('w-40')}{bar('w-36')}</div>
      </div>
      {/* Parties */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-8 border-b border-cardBg">
        <div className="space-y-2">{bar('w-16')}{bar('w-32')}{bar('w-40')}</div>
        <div className="space-y-2">{bar('w-16')}{bar('w-48')}{bar('w-36')}{bar('w-24')}</div>
      </div>
      {/* Table rows */}
      <div className="space-y-3 pb-8 border-b border-cardBg">
        {[1, 2].map((i) => (
          <div key={i} className="flex justify-between">{bar('w-1/2')}{bar('w-16')}</div>
        ))}
      </div>
      {/* Payment */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => <div key={i} className="space-y-2">{bar('w-16')}{bar('w-24')}</div>)}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Error state
// ─────────────────────────────────────────────────────────────────────────────

function ReceiptError({ message, onRetry, strings }) {
  return (
    <div className="bg-surface rounded-2xl shadow-card p-8 md:p-12 text-center">
      <p className="text-2xl font-heading text-headingText mb-3">{strings.receipt.errorTitle}</p>
      <p className="text-textSecondary font-sans text-sm mb-6 max-w-sm mx-auto">{message}</p>
      {onRetry && (
        <PanchTatvaButton variant="outline" size="default" onClick={onRetry}>
          {strings.receipt.tryAgain}
        </PanchTatvaButton>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export default function Receipt() {
  const strings = useStrings();
  const { id } = useParams();
  const navigate = useNavigate();
  const { getIdToken } = useAuth();

  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  // errorType drives which message is shown; null = no error
  const [errorType, setErrorType] = useState(null); // 'notFound'|'forbidden'|'rateLimit'|'disabled'|'generic'
  const [pdfError, setPdfError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchReceipt = useCallback(async () => {
    setLoading(true);
    setErrorType(null);
    try {
      const idToken = await getIdToken();
      const data = await getReceipt(id, idToken);
      setReceipt(data);
    } catch (err) {
      if (err instanceof ReceiptDisabledError) {
        setErrorType('disabled'); // hide silently — show nothing
      } else if (err instanceof ReceiptRateLimitError) {
        setErrorType('rateLimit');
      } else if (err instanceof ReceiptForbiddenError) {
        setErrorType('forbidden');
      } else if (err instanceof ReceiptNotFoundError) {
        setErrorType('notFound');
      } else {
        setErrorType('generic');
      }
    } finally {
      setLoading(false);
    }
  }, [id, getIdToken]);

  useEffect(() => {
    fetchReceipt();
  }, [fetchReceipt]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    setPdfError(null);
    setPdfLoading(true);
    try {
      const idToken = await getIdToken();
      await downloadReceiptPdf(id, idToken);
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('429') || msg.toLowerCase().includes('rate')) {
        setPdfError(strings.receipt.pdfRateLimit);
      } else {
        setPdfError(strings.receipt.pdfError);
      }
    } finally {
      setPdfLoading(false);
    }
  };

  // 503 — feature disabled: show nothing at all
  if (!loading && errorType === 'disabled') {
    return null;
  }

  // Determine error message string
  const errorMessage = errorType === 'notFound'  ? strings.receipt.errorNotFound
    : errorType === 'forbidden'  ? strings.receipt.errorForbidden
    : errorType === 'rateLimit'  ? strings.receipt.errorRateLimit
    : errorType === 'generic'    ? strings.receipt.errorGeneric
    : null;

  return (
    <>
      {/* ── Print-only styles ─────────────────────────────────────────────── */}
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-container { box-shadow: none !important; border: none !important; }
        }
      `}</style>

      <div className="w-full max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-20 fade-section show">

        {/* Action bar — hidden on print, hidden while loading/error */}
        {!loading && !errorType && (
          <div className="no-print flex flex-wrap items-center justify-between gap-3 mb-8">
            <button
              onClick={() => navigate(`/orders/${id}`)}
              className="text-sm text-secondary font-semibold font-sans hover:underline flex items-center gap-1"
            >
              ← {strings.receipt.backToOrder}
            </button>
            <div className="flex flex-col items-end gap-2">
              <div className="flex gap-3">
                <PanchTatvaButton variant="outline" size="small" onClick={handlePrint}>
                  {strings.receipt.printReceipt}
                </PanchTatvaButton>
                <PanchTatvaButton
                  variant="primary"
                  size="small"
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading}
                >
                  {pdfLoading ? '…' : strings.receipt.downloadPdf}
                </PanchTatvaButton>
              </div>
              {pdfError && (
                <p className="text-xs text-red-600 font-sans">{pdfError}</p>
              )}
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && <ReceiptSkeleton />}

        {/* Error */}
        {!loading && errorType && errorType !== 'disabled' && (
          <ReceiptError
            message={errorMessage}
            onRetry={errorType === 'generic' ? fetchReceipt : undefined}
            strings={strings}
          />
        )}

        {/* Receipt card */}
        {!loading && !errorType && receipt && (
          <div className="print-container bg-surface rounded-2xl shadow-card p-6 md:p-10">
            <ReceiptHeader receipt={receipt} strings={strings} />
            <ReceiptParties receipt={receipt} strings={strings} />
            <ReceiptTable receipt={receipt} strings={strings} />
            <ReceiptPayment receipt={receipt} strings={strings} />
          </div>
        )}
      </div>
    </>
  );
}
